
import { reactive } from 'vue';
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';

import VButtonIcon from '@/components/VButton/VButtonIcon.vue';
import VGanttCanvas from './VGanttCanvas.vue';

import { PMStructureGanttScaleTypeEnum } from '@/ship/Enums/ProjectManagementEnums';
import { DatesParams, DateTitle, GanttNewLink, HeaderTitle, Structure } from '@/ship/Models/ProjectManagement';
import { cloneDeep } from 'lodash';

import ZoomInSVG from '@/assets/zoom_in.svg';
import ZoomOutSVG from '@/assets/zoom_out.svg';

@Component({
    components: {
        VButtonIcon,
        VGanttCanvas,
        ZoomInSVG,
        ZoomOutSVG,
    },
})
export default class VGantt extends Vue {
    @Prop({ type: Array, default: () => [] }) tasks!: Structure[];
    @Prop({ type: Number, default: 0 }) scrollTop!: number;
    @Prop({ type: Number, default: 0 }) offsetHeight!: number;
    @Prop({ type: Number }) scaleType!: PMStructureGanttScaleTypeEnum;
    @Prop({ type: Boolean, default: true }) canSelectByClick!: boolean;
    @Prop({ type: Boolean, default: true }) isZoomAvailable!: boolean;

    @Ref() refGantt!: HTMLDivElement;

    headerTitles: HeaderTitle[] = [];
    datesTitles: DateTitle[] = [];
    step: number = 40;
    stepChangeValue = 5;
    currentDatePosition = -100;

    private readonly DAYS_DEFAULT_WIDTH = 40;
    private readonly WEEKS_DEFAULT_WIDTH = 80;
    private readonly MONTHS_DEFAULT_WIDTH = 56;
    private readonly QUARTERS_DEFAULT_WIDTH = 56;
    private readonly DAYS_IN_WEEK = 7;

    private readonly MONTH_WEEK_SCALE_NAMES: string[] = [
        'янв',
        'фев',
        'мар',
        'апр',
        'май',
        'июн',
        'июл',
        'авг',
        'сен',
        'окт',
        'ноя',
        'дек',
    ];

    private readonly MONTH_MONTH_SCALE_NAMES: string[] = [
        'Янв',
        'Фев',
        'Март',
        'Апр',
        'Май',
        'Июнь',
        'Июль',
        'Авг',
        'Сен',
        'Окт',
        'Ноя',
        'Дек',
    ];

    private readonly MONTH_DAY_SCALE_NAMES: string[] = [
        'Январь',
        'Февраль',
        'Март',
        'Апрель',
        'Май',
        'Июнь',
        'Июль',
        'Август',
        'Сентябрь',
        'Октябрь',
        'Ноябрь',
        'Декабрь',
    ];

    datesParams: DatesParams = {
        minDate: null,
        weeksAmount: null,
    };

    params = reactive({
        offsetHeight: 0,
        offsetWidth: 0,
        scrollLeft: 0,
        scrollTop: 0,
    });

    monthName(array: string[], date: Date) {
        return array[date.getMonth()];
    }

    /**
     * @description get min and max dates from child component
     * @param {Object} data - contains minDate and weeksCount
     * @param {Date} data.minDate - start date of the project
     * @param {number} data.weeksCount - number of weeks in the project
     * @returns {void}
     */
    renderDates(data: DatesParams): void {
        if (this.datesParams.minDate === null) this.datesParams = data;
        const copyDatesParams = cloneDeep(this.datesParams);
        this.headerTitles = [];
        this.datesTitles = [];
        let latestDate: Date = copyDatesParams.minDate!;

        const minDate = new Date(copyDatesParams.minDate!);

        function daysInMonth(date: Date) {
            return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        }

        switch (this.scaleType) {
            case PMStructureGanttScaleTypeEnum.Days:
                this.step = this.step < this.DAYS_DEFAULT_WIDTH ? this.DAYS_DEFAULT_WIDTH : this.step;
                copyDatesParams.minDate!.setDate(copyDatesParams.minDate!.getDate() - 1);
                for (let i = 1; i <= copyDatesParams.weeksAmount! + 1; i++) {
                    for (let j = 0; j < this.DAYS_IN_WEEK; j++) {
                        const date = new Date(copyDatesParams.minDate!.setDate(copyDatesParams.minDate!.getDate() + 1));
                        this.datesTitles.push({
                            title: String(date.getDate()),
                            quartStart: date.getMonth() % 3 === 0 && date.getDate() === 1,
                        });
                        if (date.getDate() === 1 || !this.headerTitles.length) {
                            this.headerTitles.push({
                                title: this.monthName(this.MONTH_DAY_SCALE_NAMES, date) + ' ' + date.getFullYear(),
                                spacing: this.step * (daysInMonth(date) - date.getDate()) + this.step,
                            });
                        }
                        latestDate = date;
                    }
                }
                this.headerTitles.at(-1)!.spacing = this.step * latestDate.getDate();
                break;

            case PMStructureGanttScaleTypeEnum.Weeks: {
                this.step = this.step < this.WEEKS_DEFAULT_WIDTH ? this.WEEKS_DEFAULT_WIDTH : this.step;
                copyDatesParams.minDate!.setDate(copyDatesParams.minDate!.getDate() - this.DAYS_IN_WEEK);
                let lastYear = copyDatesParams.minDate!.getFullYear();
                for (let i = 1; i <= copyDatesParams.weeksAmount! + 1; i++) {
                    const date = new Date(
                        copyDatesParams.minDate!.setDate(copyDatesParams.minDate!.getDate() + this.DAYS_IN_WEEK),
                    );

                    const startWeekDate = date.getDate();
                    const endWeekDate = date;
                    endWeekDate.setDate(date.getDate() + this.DAYS_IN_WEEK - 1);
                    this.datesTitles.push({
                        title: String(
                            startWeekDate +
                                ' - ' +
                                endWeekDate.getDate() +
                                ' ' +
                                this.monthName(this.MONTH_WEEK_SCALE_NAMES, endWeekDate),
                        ),
                        quartStart:
                            endWeekDate.getMonth() % 3 === 0 &&
                            24 <= daysInMonth(endWeekDate) - endWeekDate.getDate() &&
                            endWeekDate.getDate() <= 7,
                    });

                    if (
                        (endWeekDate.getMonth() === 0 && endWeekDate.getFullYear() !== lastYear) ||
                        !this.headerTitles.length
                    ) {
                        this.headerTitles.push({
                            title: endWeekDate.getFullYear() + ' год',
                            spacing:
                                endWeekDate.getMonth() === 0
                                    ? this.step * 52
                                    : this.step * (52 - this.getWeekNumber(endWeekDate) + 1),
                        });
                        lastYear = endWeekDate.getFullYear();
                    }
                    latestDate = endWeekDate;
                }
                this.headerTitles.at(-1)!.spacing = this.step * this.getWeekNumber(latestDate);
                break;
            }

            case PMStructureGanttScaleTypeEnum.Months: {
                this.step = this.step < this.MONTHS_DEFAULT_WIDTH ? this.MONTHS_DEFAULT_WIDTH : this.step;
                const lastDate = new Date(copyDatesParams.minDate!);
                lastDate.setDate(lastDate.getDate() + copyDatesParams.weeksAmount! * this.DAYS_IN_WEEK);
                const lastDateTime = lastDate.getTime();
                copyDatesParams.minDate!.setDate(
                    copyDatesParams.minDate!.getDate() - daysInMonth(copyDatesParams.minDate!),
                );

                while (copyDatesParams.minDate!.getTime() < lastDateTime) {
                    copyDatesParams.minDate!.setFullYear(copyDatesParams.minDate!.getFullYear());
                    copyDatesParams.minDate!.setMonth(copyDatesParams.minDate!.getMonth());
                    copyDatesParams.minDate!.setDate(1);
                    const date = new Date(
                        copyDatesParams.minDate!.setDate(
                            copyDatesParams.minDate!.getDate() + daysInMonth(copyDatesParams.minDate!),
                        ),
                    );
                    this.datesTitles.push({
                        title: this.monthName(this.MONTH_MONTH_SCALE_NAMES, date),
                        quartStart: date.getMonth() % 3 === 0,
                    });
                    if (date.getMonth() === 0 || !this.headerTitles.length) {
                        this.headerTitles.push({
                            title: date.getFullYear() + ' год',
                            spacing: this.step * (12 - date.getMonth()),
                        });
                    }
                    latestDate = date;
                }
                this.headerTitles.at(-1)!.spacing = this.step * (latestDate.getMonth() + 1);
                break;
            }

            case PMStructureGanttScaleTypeEnum.Quarters: {
                this.step = this.step < this.QUARTERS_DEFAULT_WIDTH ? this.QUARTERS_DEFAULT_WIDTH : this.step;
                const lastDate = new Date(copyDatesParams.minDate!);
                lastDate.setDate(lastDate.getDate() + copyDatesParams.weeksAmount! * this.DAYS_IN_WEEK);
                const lastDateTime = lastDate.getTime();
                copyDatesParams.minDate!.setDate(
                    copyDatesParams.minDate!.getDate() - daysInMonth(copyDatesParams.minDate!),
                );

                while (copyDatesParams.minDate!.getTime() < lastDateTime) {
                    copyDatesParams.minDate!.setFullYear(copyDatesParams.minDate!.getFullYear());
                    copyDatesParams.minDate!.setMonth(copyDatesParams.minDate!.getMonth());
                    copyDatesParams.minDate!.setDate(1);
                    const date = new Date(
                        copyDatesParams.minDate!.setDate(
                            copyDatesParams.minDate!.getDate() + daysInMonth(copyDatesParams.minDate!),
                        ),
                    );
                    this.datesTitles.push({
                        title: String(date.getMonth() + 1),
                        quartStart: date.getMonth() % 3 === 0,
                    });
                    if (
                        date.getMonth() === 0 ||
                        date.getMonth() === 3 ||
                        date.getMonth() === 6 ||
                        date.getMonth() === 9 ||
                        !this.headerTitles.length
                    ) {
                        this.headerTitles.push({
                            title: Math.trunc(date.getMonth() / 3 + 1) + ' кв ' + date.getFullYear(),
                            spacing: this.step * (3 - (date.getMonth() % 3)),
                        });
                    }
                    latestDate = date;
                }
                this.headerTitles.at(-1)!.spacing = this.step * (latestDate.getMonth() % 3);
                break;
            }

            default:
                break;
        }

        function daysInMonthByYear(year: number, month: number): number {
            return new Date(year, month + 1, 0).getDate();
        }

        const currentDate = new Date();
        if (currentDate.getTime() < latestDate.getTime()) {
            const dayMs = 1000 * 3600 * 24;
            const daysFromMinDate = Math.floor((currentDate.getTime() - minDate.getTime()) / dayMs);

            switch (this.scaleType) {
                case PMStructureGanttScaleTypeEnum.Quarters:
                case PMStructureGanttScaleTypeEnum.Months: {
                    const startDate = new Date(this.datesParams.minDate!);
                    const targetDate = new Date(currentDate);

                    const startYear = startDate.getFullYear();
                    const startMonth = startDate.getMonth();

                    const targetYear = targetDate.getFullYear();
                    const targetMonth = targetDate.getMonth();
                    const targetDayOfMonth = targetDate.getDate();

                    const fullMonthsPassed = (targetYear - startYear) * 12 + (targetMonth - startMonth);

                    const daysInTargetMonth = daysInMonthByYear(targetYear, targetMonth);

                    const fractionOfCurrentMonth = (targetDayOfMonth - 1) / daysInTargetMonth;

                    const effectiveMonths = fullMonthsPassed + fractionOfCurrentMonth;

                    this.currentDatePosition = effectiveMonths * this.step;
                    break;
                }
                default:
                    this.currentDatePosition = daysFromMinDate * this.dateScale;
                    break;
            }
        }
    }

    getWeekNumber(date: Date): number {
        const DAYS_IN_A_WEEK = 7;
        const DAYS_IN_MILLISECONDS = 1000 * 60 * 60 * 24;
        const MONDAY_OFFSET = 1;

        const currentWeekDate = new Date(date.valueOf());
        currentWeekDate.setDate(
            currentWeekDate.getDate() + MONDAY_OFFSET - (currentWeekDate.getDay() || DAYS_IN_A_WEEK),
        );

        // Get the first day of the year
        const yearStart = new Date(currentWeekDate.getFullYear(), 0, 1);

        // Calculate the number of weeks between the two dates
        return Math.ceil(
            ((currentWeekDate.getTime() - yearStart.getTime()) / DAYS_IN_MILLISECONDS + 1) / DAYS_IN_A_WEEK,
        );
    }

    get isZoomDisabled() {
        switch (this.scaleType) {
            case PMStructureGanttScaleTypeEnum.Days:
                return this.step - this.DAYS_DEFAULT_WIDTH === 0;
            case PMStructureGanttScaleTypeEnum.Weeks:
                return this.step - this.WEEKS_DEFAULT_WIDTH === 0;
            case PMStructureGanttScaleTypeEnum.Months:
                return this.step - this.MONTHS_DEFAULT_WIDTH === 0;
            case PMStructureGanttScaleTypeEnum.Quarters:
                return this.step - this.QUARTERS_DEFAULT_WIDTH === 0;
            default:
                return false;
        }
    }

    get dateScale() {
        switch (this.scaleType) {
            case PMStructureGanttScaleTypeEnum.Days: {
                return this.step;
            }
            case PMStructureGanttScaleTypeEnum.Weeks: {
                return this.step / 7;
            }
            case PMStructureGanttScaleTypeEnum.Quarters:
            case PMStructureGanttScaleTypeEnum.Months: {
                return this.step / 30;
            }
            default:
                return 0;
        }
    }

    get currentDateStyle() {
        return {
            left: `${this.currentDatePosition}px`,
            height: `${this.params.offsetHeight}px`,
        };
    }

    changeScale(isIncreasing: boolean) {
        this.step += isIncreasing ? this.stepChangeValue : -this.stepChangeValue;
    }

    createLinkFromGantt(tasks: GanttNewLink) {
        this.$emit('createLinkFromGantt', tasks);
    }

    @Watch('scrollTop', { immediate: true })
    scroll() {
        this.params.scrollTop = this.scrollTop;
        if (this.refGantt) {
            this.params.scrollLeft = this.refGantt.scrollLeft;
            this.params.offsetWidth = this.refGantt.offsetWidth;
            this.params.offsetHeight = this.refGantt.offsetHeight;
        }
    }

    scrollParam(event: Event) {
        const { offsetHeight, offsetWidth, scrollLeft } = event.target as HTMLElement;

        this.params.offsetHeight = offsetHeight;
        this.params.offsetWidth = offsetWidth;
        this.params.scrollLeft = scrollLeft;
    }
}
